import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip,
  MenuItem,
  Snackbar,
} from "@mui/material";
import { getTimeSeries, getFeatureSummary, getTrace, retryAnnouncementSummary, lookupAnnouncementByUrl, getApiEnv, setApiEnv } from "../api/aiUsageApi";
import "../rag-docs-dashboard/rag-docs-dashboard.css";
import "./ai-usage-dashboard.css";

/* ── helpers ──────────────────────────────────────────────────────────── */
const fmtTokens = (n) => {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
};
const fmtCost = (v, dec = 4) =>
  v == null ? "—" : `$${Number(v).toFixed(dec)}`;
const fmtNum = (n) =>
  n == null ? "—" : Number(n).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const GRANULARITIES = ["HOUR", "DAY", "WEEK", "MONTH"];

const FEATURE_COLORS = [
  "#1976d2", "#388e3c", "#f57c00", "#7b1fa2",
  "#c62828", "#0288d1", "#558b2f", "#ad1457",
];

/** Extract first https?:// URL from an error message string */
const extractUrl = (msg) => {
  if (!msg) return "";
  const m = msg.match(/https?:\/\/[^\s,]+/);
  return m ? m[0].replace(/:$/, "") : "";
};

/** Format a LocalDateTime array or string from the lookup DTO */
const fmtLocalDt = (dt) => {
  if (!dt) return "";
  // Spring may return as array [y,m,d,h,min,s] or ISO string
  if (Array.isArray(dt)) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = dt;
    return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}T${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  // already a string — strip trailing Z if any
  return String(dt).replace("Z", "").substring(0, 19);
};

/* ── Announcement Lookup Panel ───────────────────────────────────────── */
function LookupPanel({ errorMessage, onSuccess, onSnackbar }) {
  const [url, setUrl] = useState(() => extractUrl(errorMessage));
  const [looking, setLooking] = useState(false);
  const [result, setResult] = useState(null);   // AnnouncementLookupDto
  const [generating, setGenerating] = useState(false);

  const handleLookup = async () => {
    if (!url.trim()) return;
    setLooking(true);
    setResult(null);
    const res = await lookupAnnouncementByUrl(url.trim());
    setLooking(false);
    if (res.status && res.data?.data?.length > 0) {
      setResult(res.data.data[0]);
    } else {
      onSnackbar({ open: true, msg: "No announcement found for that URL", severity: "warning" });
    }
  };

  const handleGenerate = async () => {
    if (!result) return;
    const broadcastDateTime = fmtLocalDt(result.broadcastDateTime);
    setGenerating(true);
    const res = await retryAnnouncementSummary({
      symbol: result.symbol,
      subject: result.subject,
      broadcastDateTime,
      sendAlert: false,
    });
    setGenerating(false);
    if (res.status && res.data?.success) {
      onSnackbar({ open: true, msg: `✓ Summary generated for ${result.symbol}`, severity: "success" });
      onSuccess();
    } else {
      const errMsg = res.data?.message || res.error || "Failed";
      onSnackbar({ open: true, msg: `✗ ${errMsg}`, severity: "error" });
    }
  };

  return (
    <Box sx={{
      p: 2, background: "rgba(253,237,237,0.6)",
      borderTop: "1px dashed #f5c6c6",
      display: "flex", flexDirection: "column", gap: 1.5,
    }}>
      {/* URL input row */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#c62828", whiteSpace: "nowrap" }}>
          Attachment URL:
        </Typography>
        <TextField
          size="small" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://nsearchives.nseindia.com/..."
          sx={{ flex: 1, minWidth: 320, "& .MuiOutlinedInput-root": { borderRadius: "7px", fontSize: "0.78rem" } }}
        />
        <Button
          size="small" variant="contained" color="error"
          disabled={!url.trim() || looking}
          onClick={handleLookup}
          sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "none", borderRadius: "7px", whiteSpace: "nowrap" }}
        >
          {looking ? <CircularProgress size={12} color="inherit" /> : "🔍 Lookup"}
        </Button>
      </Box>

      {/* Lookup result */}
      {result && (
        <Box sx={{
          display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap",
          p: 1.5, borderRadius: "8px",
          background: result.hasSummary ? "rgba(232,245,233,0.9)" : "rgba(255,248,225,0.9)",
          border: `1px solid ${result.hasSummary ? "#a5d6a7" : "#ffe082"}`,
        }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "0.75rem" }}>
                <strong>Symbol:</strong> {result.symbol}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem" }}>
                <strong>Company:</strong> {result.companyName || "—"}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem" }}>
                <strong>Broadcast:</strong> {fmtLocalDt(result.broadcastDateTime).replace("T", " ")}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <strong>Subject:</strong> {result.subject}
              </Typography>
            </Box>
            <Typography sx={{
              fontSize: "0.7rem", fontWeight: 700,
              color: result.hasSummary ? "#2e7d32" : "#f57f17"
            }}>
              {result.hasSummary ? "✓ Summary already exists" : "⚠ No summary yet"}
            </Typography>
          </Box>
          <Button
            size="small" variant="contained"
            color={result.hasSummary ? "success" : "warning"}
            disabled={generating}
            onClick={handleGenerate}
            sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "none", borderRadius: "7px", whiteSpace: "nowrap", ml: "auto" }}
          >
            {generating
              ? <CircularProgress size={12} color="inherit" />
              : result.hasSummary ? "↺ Regenerate" : "⚡ Generate Summary"}
          </Button>
        </Box>
      )}
    </Box>
  );
}

/* ── small bar chart (no extra lib needed) ────────────────────────────── */
function BarChart({ data, metric }) {
  if (!data || data.length === 0)
    return <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>No data</Typography>;

  const values = data.map((d) => Number(d[metric]) || 0);
  const maxVal = Math.max(...values, 1);

  return (
    <div className="chart-container">
      <div className="bar-chart">
        {data.map((d, i) => {
          const pct = (values[i] / maxVal) * 100;
          return (
            <div key={i} className="bar-group bar-tooltip-wrapper">
              <div className="bar-tooltip">
                <strong>{d.period}</strong><br />
                Cost: {fmtCost(d.totalCost, 5)}<br />
                Calls: {fmtNum(d.calls)}<br />
                Tokens in: {fmtTokens(d.inputTokens)}<br />
                Tokens out: {fmtTokens(d.outputTokens)}<br />
                Errors: {fmtNum(d.errorCalls)}
              </div>
              <div
                className="bar-fill"
                style={{ height: `${Math.max(pct, 1)}%` }}
              />
              <span className="bar-label" title={d.period}>{d.period}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── main component ──────────────────────────────────────────────────── */
export default function AiUsageDashboard() {
  /* filter state */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [granularity, setGranularity] = useState("DAY");
  const [featureFilter, setFeatureFilter] = useState("ANNOUNCEMENT_SUMMARY");
  const [chartMetric, setChartMetric] = useState("totalCost");
  const [apiEnv, setApiEnvState] = useState(typeof getApiEnv === "function" ? getApiEnv() : "dev");

  /* timeseries */
  const [tsData, setTsData] = useState([]);
  const [tsLoading, setTsLoading] = useState(true);
  const [tsError, setTsError] = useState(false);

  /* features breakdown */
  const [featData, setFeatData] = useState([]);
  const [featLoading, setFeatLoading] = useState(true);
  const [featError, setFeatError] = useState(false);

  /* trace */
  const [traceData, setTraceData] = useState([]);
  const [traceTotal, setTraceTotal] = useState(0);
  const [tracePage, setTracePage] = useState(0);
  const [traceSize, setTraceSize] = useState(25);
  const [traceLoading, setTraceLoading] = useState(true);
  const [traceError, setTraceError] = useState(false);

  /* expand */
  const [expandedGroupKey, setExpandedGroupKey] = useState(null);
  const [expandedFixKey, setExpandedFixKey] = useState(null);  // independent: which usage's Fix panel is open
  const [regenLoadingIds, setRegenLoadingIds] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

  /* ── loaders ─────────────────────────────────────────────────────── */
  const buildBaseParams = useCallback(() => ({
    from: fromDate || undefined,
    to: toDate || undefined,
    feature: featureFilter || undefined,
  }), [fromDate, toDate, featureFilter]);

  const canLoad = Boolean(featureFilter);

  const loadTimeSeries = useCallback(async (extraParams = {}) => {
    setTsLoading(true); setTsError(false);
    const res = await getTimeSeries({ ...buildBaseParams(), granularity, ...extraParams });
    if (res.status) setTsData(res.data || []);
    else setTsError(true);
    setTsLoading(false);
  }, [buildBaseParams, granularity]);

  const loadFeatures = useCallback(async (extraParams = {}) => {
    setFeatLoading(true); setFeatError(false);
    const res = await getFeatureSummary({ ...buildBaseParams(), ...extraParams });
    if (res.status) setFeatData(res.data || []);
    else setFeatError(true);
    setFeatLoading(false);
  }, [buildBaseParams]);

  const loadTrace = useCallback(async (page = tracePage, size = traceSize, extraParams = {}) => {
    setTraceLoading(true); setTraceError(false);
    const res = await getTrace({ ...buildBaseParams(), page, size, ...extraParams });
    if (res.status) {
      const d = res.data;
      if (d && d.page !== undefined) {
        // Sort groups by broadcastDateTime descending (nulls last)
        const sorted = (d.content || []).slice().sort((a, b) => {
          if (!a.broadcastDateTime && !b.broadcastDateTime) return 0;
          if (!a.broadcastDateTime) return 1;
          if (!b.broadcastDateTime) return -1;
          return new Date(b.broadcastDateTime) - new Date(a.broadcastDateTime);
        });
        setTraceData(sorted);
        setTraceTotal(d.page.totalElements || 0);
      } else if (Array.isArray(d)) {
        setTraceData(d);
        setTraceTotal(d.length);
      } else {
        setTraceData(d.content || []);
        setTraceTotal(d.totalElements || 0);
      }
    } else {
      setTraceError(true);
    }
    setTraceLoading(false);
  }, [buildBaseParams, tracePage, traceSize]);

  useEffect(() => {
    if (!featureFilter) return;  // feature is mandatory
    loadTimeSeries();
    loadFeatures();
    loadTrace(0, traceSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── apply filters ─────────────────────────────────────────────── */
  const handleApply = () => {
    setTracePage(0);
    loadTimeSeries();
    loadFeatures();
    loadTrace(0, traceSize);
  };

  /* ── env toggle ────────────────────────────────────────── */
  const handleEnvToggle = () => {
    const next = apiEnv === "dev" ? "prod" : "dev";
    if (typeof setApiEnv === "function") setApiEnv(next);
    setApiEnvState(next);
    setTracePage(0);
    // Reload everything from the new env
    setTimeout(() => {
      loadTimeSeries();
      loadFeatures();
      loadTrace(0, traceSize);
    }, 0);
  };

  const handleClear = () => {
    setFromDate(""); setToDate(""); setGranularity("DAY");  // feature stays — it's mandatory
    setTracePage(0);
    setTimeout(() => {
      loadTimeSeries({ from: undefined, to: undefined, granularity: "DAY" });
      loadFeatures({ from: undefined, to: undefined });
      loadTrace(0, traceSize, { from: undefined, to: undefined });
    }, 0);
  };

  const handleGranularityChange = (g) => {
    setGranularity(g);
    loadTimeSeries({ granularity: g });
  };

  /* ── trace pagination ───────────────────────────────────────────── */
  const handleTracePage = (_, newPage) => {
    setTracePage(newPage);
    loadTrace(newPage, traceSize);
  };
  const handleTraceSize = (e) => {
    const s = parseInt(e.target.value, 10);
    setTraceSize(s); setTracePage(0);
    loadTrace(0, s);
  };



  /* ── regenerate summary handler ──────────────────────────────────── */
  const handleRegen = async (group, uid) => {
    const broadcastDateTime = fmtLocalDt(group.broadcastDateTime);
    setRegenLoadingIds(prev => new Set(prev).add(uid));
    const res = await retryAnnouncementSummary({
      symbol: group.symbol,
      subject: group.subject,
      broadcastDateTime,
      sendAlert: false,
    });
    setRegenLoadingIds(prev => { const s = new Set(prev); s.delete(uid); return s; });
    if (res.status && res.data?.success) {
      setSnackbar({ open: true, msg: `✓ Summary regenerated for ${group.symbol}`, severity: "success" });
    } else {
      const errMsg = res.data?.message || res.error || "Failed";
      setSnackbar({ open: true, msg: `✗ ${errMsg}`, severity: "error" });
    }
  };

  /* ── aggregate totals from features ──────────────────────────────── */
  const totalCost = featData.reduce((s, f) => s + (Number(f.totalCost) || 0), 0);
  const totalIn = featData.reduce((s, f) => s + (Number(f.inputTokens) || 0), 0);
  const totalOut = featData.reduce((s, f) => s + (Number(f.outputTokens) || 0), 0);
  const totalCalls = featData.reduce((s, f) => s + (Number(f.calls) || 0), 0);
  const tsTotalErrors = tsData.reduce((s, d) => s + (Number(d.errorCalls) || 0), 0);

  const isFilterActive = fromDate || toDate;

  /* ── known feature list (static so dropdown is always available) ── */
  const KNOWN_FEATURES = [
    "ANNOUNCEMENT_SUMMARY"
  ];
  const featureOptions = KNOWN_FEATURES;

  return (
    <Box className="page-content-width page-bg admin-page" style={{ padding: "30px" }}>
      {/* ── Header ───────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3.5, mt: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: "-0.03em", color: "text.primary" }}>
            AI Usage Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Token consumption, costs &amp; call traces across all AI features
          </Typography>
        </Box>

        {/* ── Dev / Prod toggle ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Box
            onClick={handleEnvToggle}
            title={apiEnv === "dev"
              ? "Pointing to DEV — click to switch to PROD"
              : "Pointing to PROD — click to switch to DEV"}
            sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              cursor: "pointer", userSelect: "none",
              border: "2px solid",
              borderColor: apiEnv === "dev" ? "#f59e0b" : "#10b981",
              borderRadius: "20px",
              px: 1.5, py: 0.4,
              background: apiEnv === "dev" ? "rgba(245,158,11,0.08)" : "rgba(16,185,129,0.08)",
              transition: "all 0.2s",
              "&:hover": { opacity: 0.8 },
            }}
          >
            {/* Toggle track */}
            <Box sx={{ position: "relative", width: 32, height: 16 }}>
              <Box sx={{
                position: "absolute", inset: 0, borderRadius: "8px",
                background: apiEnv === "dev" ? "#fbbf24" : "#10b981",
                transition: "background 0.2s",
              }} />
              <Box sx={{
                position: "absolute", top: 2,
                left: apiEnv === "dev" ? 2 : 16,
                width: 12, height: 12, borderRadius: "50%",
                background: "#fff",
                transition: "left 0.2s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
              }} />
            </Box>
            <Typography sx={{
              fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.04em",
              color: apiEnv === "dev" ? "#b45309" : "#059669",
            }}>
              {apiEnv === "dev" ? "DEV" : "PROD"}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
            {apiEnv === "dev" ? "dev.equitypicker.com" : "ai-api.investobull.com"}
          </Typography>
        </Box>
      </Box>

      {/* ── Filter Bar ──────────────────────────────────────────────── */}
      <Box
        className="admin-card mb-3"
        sx={{
          display: "flex", alignItems: "center", gap: 2,
          flexWrap: "wrap", p: 2, borderRadius: "16px", mb: 3,
        }}
      >
        {/* From */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography component="label" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
            From:
          </Typography>
          <TextField type="date" size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            sx={{ width: "140px", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            InputLabelProps={{ shrink: true }} />
        </Box>

        {/* To */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography component="label" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
            To:
          </Typography>
          <TextField type="date" size="small" value={toDate} onChange={(e) => setToDate(e.target.value)}
            sx={{ width: "140px", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            InputLabelProps={{ shrink: true }} />
        </Box>

        {/* Feature filter */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography component="label" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
            Feature:
          </Typography>
          <TextField select size="small" value={featureFilter}
            onChange={(e) => setFeatureFilter(e.target.value)}
            error={!featureFilter}
            helperText={!featureFilter ? "Required" : ""}
            sx={{ width: "220px", "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}>
            {featureOptions.map((f) => (
              <MenuItem key={f} value={f}>{f}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Button variant="contained" size="small" onClick={handleApply}
          disabled={!featureFilter}
          title={!featureFilter ? "Select a feature first" : ""}
          sx={{ borderRadius: "8px", fontWeight: 700, px: 2.5, py: 0.8, textTransform: "none" }}>
          Apply
        </Button>
        {isFilterActive && (
          <Button variant="outlined" color="inherit" size="small" onClick={handleClear}
            sx={{ borderRadius: "8px", fontWeight: 600, px: 2.5, py: 0.8, textTransform: "none", borderColor: "divider" }}>
            Clear Dates
          </Button>
        )}
      </Box>

      {/* ── KPI Cards ─────────────────────────────────────────────────── */}
      {featLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={36} /></Box>
      ) : featError ? (
        <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>Failed to load feature summary.</Alert>
      ) : (
        <>
          <Typography className="section-label" component="p" sx={{ mb: 1 }}>Overview</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <Box className="stat-card stat-cost" sx={{ minWidth: "150px", flex: "1 0 auto" }}>
              <Typography className="stat-val">{fmtCost(totalCost, 4)}</Typography>
              <Typography className="stat-lbl">Total Cost (USD)</Typography>
            </Box>
            <Box className="stat-card stat-calls" sx={{ minWidth: "150px", flex: "1 0 auto" }}>
              <Typography className="stat-val">{fmtNum(totalCalls)}</Typography>
              <Typography className="stat-lbl">Total Calls</Typography>
            </Box>
            <Box className="stat-card" sx={{ minWidth: "150px", flex: "1 0 auto" }}>
              <Typography className="stat-val">{fmtTokens(totalIn)}</Typography>
              <Typography className="stat-lbl">Input Tokens</Typography>
            </Box>
            <Box className="stat-card" sx={{ minWidth: "150px", flex: "1 0 auto" }}>
              <Typography className="stat-val">{fmtTokens(totalOut)}</Typography>
              <Typography className="stat-lbl">Output Tokens</Typography>
            </Box>
            <Box className="stat-card stat-errors" sx={{ minWidth: "150px", flex: "1 0 auto" }}>
              <Typography className="stat-val">{fmtNum(tsTotalErrors)}</Typography>
              <Typography className="stat-lbl">Error Calls</Typography>
            </Box>
          </Box>
        </>
      )}

      {/* ── Timeseries Chart ─────────────────────────────────────────── */}
      <Box className="admin-card" sx={{ mb: 3, p: 2.5, borderRadius: "16px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
            Usage Over Time
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            {/* Granularity pills */}
            <Box sx={{ display: "flex", gap: 0.8 }}>
              {GRANULARITIES.map((g) => (
                <button
                  key={g}
                  className={`granularity-pill${granularity === g ? " active" : ""}`}
                  onClick={() => handleGranularityChange(g)}
                >
                  {g}
                </button>
              ))}
            </Box>

            {/* Metric selector */}
            <TextField
              select size="small" value={chartMetric}
              onChange={(e) => setChartMetric(e.target.value)}
              sx={{ width: "140px", "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8rem" } }}
            >
              <MenuItem value="totalCost">Cost (USD)</MenuItem>
              <MenuItem value="calls">Calls</MenuItem>
              <MenuItem value="inputTokens">Input Tokens</MenuItem>
              <MenuItem value="outputTokens">Output Tokens</MenuItem>
              <MenuItem value="errorCalls">Error Calls</MenuItem>
            </TextField>
          </Box>
        </Box>

        {tsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={36} /></Box>
        ) : tsError ? (
          <Alert severity="error" sx={{ borderRadius: "10px" }}>Failed to load timeseries data.</Alert>
        ) : (
          <BarChart data={tsData} metric={chartMetric} />
        )}
      </Box>

      {/* ── Feature Breakdown Table ───────────────────────────────────── */}
      <Typography className="section-label" component="p" sx={{ mb: 1 }}>Per-Feature Breakdown</Typography>
      <Box className="admin-card p-0" sx={{ border: "1px solid rgba(224,224,224,1)", mb: 3, borderRadius: "10px" }}>
        {featLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress size={34} /></Box>
        ) : featError ? (
          <Alert severity="error" sx={{ m: 2, borderRadius: "10px" }}>Failed to load feature data.</Alert>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "10px" }}>
            <Table className="table table-hover mb-0">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Feature</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Calls</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Input Tokens</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Output Tokens</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Total Cost (USD)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>% of Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {featData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", color: "text.secondary", py: 3 }}>
                      No feature data found
                    </TableCell>
                  </TableRow>
                ) : (
                  featData.map((f, i) => {
                    const pct = totalCost > 0 ? ((Number(f.totalCost) / totalCost) * 100).toFixed(1) : "0.0";
                    return (
                      <TableRow key={f.feature} hover>
                        <TableCell sx={{ color: "#94a3b8", fontWeight: 600 }}>{i + 1}</TableCell>
                        <TableCell>
                          <span className="feature-chip" style={{ background: FEATURE_COLORS[i % FEATURE_COLORS.length] + "18", color: FEATURE_COLORS[i % FEATURE_COLORS.length] }}>
                            {f.feature || "—"}
                          </span>
                        </TableCell>
                        <TableCell align="right" className="stat-calls" sx={{ fontWeight: 600 }}>{fmtNum(f.calls)}</TableCell>
                        <TableCell align="right">{fmtTokens(f.inputTokens)}</TableCell>
                        <TableCell align="right">{fmtTokens(f.outputTokens)}</TableCell>
                        <TableCell align="right" className="cost-usd">{fmtCost(f.totalCost, 5)}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                            <Box sx={{ height: 6, width: `${Math.max(Number(pct), 1)}px`, maxWidth: 80, minWidth: 4, borderRadius: 3, background: FEATURE_COLORS[i % FEATURE_COLORS.length], opacity: 0.7 }} />
                            <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>{pct}%</span>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* ── Trace Table ───────────────────────────────────────────────── */}
      <Typography className="section-label" component="p" sx={{ mb: 1 }}>Detailed Trace Log</Typography>
      <Box className="admin-card p-0" sx={{ border: "1px solid rgba(224,224,224,1)", mb: 3, borderRadius: "10px" }}>
        {traceLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress size={34} /></Box>
        ) : traceError ? (
          <Alert severity="error" sx={{ m: 2, borderRadius: "10px" }}>Failed to load trace data.</Alert>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "10px 10px 0 0" }}>
              <Table className="table mb-0" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: 32 }} />
                    <TableCell sx={{ fontWeight: "bold" }}>Broadcast (IST)</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Sentiment</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Calls</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>In Tokens</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Out Tokens</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Cost (USD)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {traceData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ textAlign: "center", color: "text.secondary", py: 3 }}>
                        No trace records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    traceData.map((group, gi) => {
                      const groupKey = `${group.broadcastDateTime || "null"}-${group.symbol || ""}-${gi}`;
                      const isExpanded = expandedGroupKey === groupKey;
                      const usages = group.usages || [];

                      // Group aggregates
                      const totalCost = usages.reduce((s, u) => s + (Number(u.cost) || 0), 0);
                      const totalIn = usages.reduce((s, u) => s + (Number(u.inputTokens) || 0), 0);
                      const totalOut = usages.reduce((s, u) => s + (Number(u.outputTokens) || 0), 0);
                      const errorCount = usages.filter(u => !u.success).length;

                      return (
                        <>
                          {/* ── Group summary row ── */}
                          <TableRow
                            key={groupKey}
                            hover
                            onClick={() => {
                              setExpandedGroupKey(isExpanded ? null : groupKey);
                              setExpandedFixKey(null); // close any open Fix panel
                            }}
                            sx={{
                              cursor: "pointer",
                              background: isExpanded ? "rgba(25,118,210,0.05)" : undefined,
                              "&:hover": { background: "rgba(25,118,210,0.04)" },
                            }}
                          >
                            {/* Expand chevron */}
                            <TableCell sx={{ textAlign: "center", color: "#94a3b8", fontSize: "0.8rem", pr: 0, width: 32 }}>
                              {isExpanded ? "▼" : "▶"}
                            </TableCell>

                            {/* Broadcast DateTime */}
                            <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.78rem", fontWeight: 600 }}>
                              {group.broadcastDateTime
                                ? group.broadcastDateTime.replace("T", " ").substring(0, 16)
                                : <span style={{ color: "#94a3b8" }}>—</span>}
                            </TableCell>

                            {/* Symbol + Company */}
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {group.symbol ? (
                                <Box>
                                  <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1565c0" }}>{group.symbol}</span>
                                  {group.company && (
                                    <Typography variant="caption" display="block" color="text.secondary"
                                      sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                      title={group.company}>{group.company}
                                    </Typography>
                                  )}
                                </Box>
                              ) : <span style={{ color: "#94a3b8" }}>—</span>}
                            </TableCell>

                            {/* Subject */}
                            <TableCell sx={{ fontSize: "0.74rem", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title={group.subject || ""}>
                              {group.subject || <span style={{ color: "#94a3b8" }}>—</span>}
                            </TableCell>

                            {/* Sentiment */}
                            <TableCell>
                              {group.sentiment ? (
                                <span style={{
                                  fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: "10px",
                                  background: group.sentiment === "Positive" ? "#e8f5e9" : group.sentiment === "Negative" ? "#ffebee" : "#fff3e0",
                                  color: group.sentiment === "Positive" ? "#2e7d32" : group.sentiment === "Negative" ? "#c62828" : "#e65100",
                                }}>{group.sentiment}</span>
                              ) : <span style={{ color: "#94a3b8" }}>—</span>}
                            </TableCell>

                            <TableCell align="right" sx={{ fontWeight: 700 }}>{usages.length}</TableCell>
                            <TableCell align="right">{fmtTokens(totalIn)}</TableCell>
                            <TableCell align="right">{fmtTokens(totalOut)}</TableCell>
                            <TableCell align="right" className="cost-usd">{fmtCost(totalCost, 5)}</TableCell>
                            <TableCell align="center">
                              {errorCount > 0
                                ? <span className="trace-error" style={{ fontSize: "0.78rem" }}>✗ {errorCount}</span>
                                : <span className="trace-success" style={{ fontSize: "0.78rem" }}>✓</span>}
                            </TableCell>
                          </TableRow>

                          {/* ── Expanded usage sub-rows ── */}
                          {isExpanded && (
                            <>
                              {/* Sub-header */}
                              <TableRow key={`${groupKey}-subhdr`} sx={{ background: "#f8fafc" }}>
                                <TableCell colSpan={2} />
                                <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Time (IST)</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Provider · Model</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Feature</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>In</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Out</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Cost</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Latency</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status / Action</TableCell>
                              </TableRow>

                              {usages.map((u) => {
                                const uid = u.id || u.usageLogId;
                                const uExpanded = expandedFixKey === `fix-${uid}`;
                                const uHasError = !u.success && u.errorMessage;
                                return (
                                  <>
                                    <TableRow key={uid} sx={{ background: "rgba(241,245,249,0.7)", "&:hover": { background: "rgba(226,232,240,0.6)" } }}>
                                      <TableCell colSpan={2} />
                                      <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.72rem" }}>{fmtDate(u.createdAt)}</TableCell>
                                      <TableCell>
                                        <Tooltip title={u.modelString || ""} placement="top">
                                          <span style={{ fontSize: "0.72rem" }}>
                                            <span style={{ fontWeight: 700, color: "#334155" }}>{u.provider || "—"}</span>
                                            {u.modelString && <span style={{ color: "#64748b" }}> · {u.modelString}</span>}
                                          </span>
                                        </Tooltip>
                                      </TableCell>
                                      <TableCell>{u.feature ? <span className="feature-chip">{u.feature}</span> : "—"}</TableCell>
                                      <TableCell align="right" sx={{ fontSize: "0.72rem" }}>{fmtTokens(u.inputTokens)}</TableCell>
                                      <TableCell align="right" sx={{ fontSize: "0.72rem" }}>{fmtTokens(u.outputTokens)}</TableCell>
                                      <TableCell align="right" className="cost-usd" sx={{ fontSize: "0.72rem" }}>{fmtCost(u.cost, 5)}</TableCell>
                                      <TableCell align="right" sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                                        {u.latencyMs != null ? `${u.latencyMs} ms` : "—"}
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                          <span className={u.success ? "trace-success" : "trace-error"} style={{ fontSize: "0.72rem" }}>
                                            {u.success ? "✓ OK" : "✗ ERR"}
                                          </span>
                                          {uHasError && (
                                            <Button size="small" variant={uExpanded ? "contained" : "outlined"} color="error"
                                              onClick={(e) => { e.stopPropagation(); setExpandedFixKey(uExpanded ? null : `fix-${uid}`); }}
                                              sx={{ fontSize: "0.65rem", fontWeight: 700, borderRadius: "6px", textTransform: "none", py: 0.2, px: 0.8 }}>
                                              {uExpanded ? "▲ Close" : "🔍 Fix"}
                                            </Button>
                                          )}
                                          {group.symbol && group.broadcastDateTime && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="secondary"
                                              disabled={regenLoadingIds.has(uid)}
                                              onClick={(e) => { e.stopPropagation(); handleRegen(group, uid); }}
                                              sx={{ fontSize: "0.65rem", fontWeight: 700, borderRadius: "6px", textTransform: "none", py: 0.2, px: 0.8, whiteSpace: "nowrap" }}
                                            >
                                              {regenLoadingIds.has(uid)
                                                ? <CircularProgress size={10} color="inherit" />
                                                : "↺ Regen"}
                                            </Button>
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>

                                    {/* Error message row */}
                                    {u.errorMessage && (
                                      <TableRow key={`${uid}-err`} sx={{ background: "rgba(255,235,238,0.5)" }}>
                                        <TableCell colSpan={2} />
                                        <TableCell colSpan={8} sx={{ fontSize: "0.7rem", color: "#c62828", py: 0.5 }}>
                                          {u.errorMessage}
                                        </TableCell>
                                      </TableRow>
                                    )}

                                    {/* LookupPanel */}
                                    {uExpanded && uHasError && (
                                      <TableRow key={`${uid}-fix`}>
                                        <TableCell colSpan={2} />
                                        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                                          <LookupPanel
                                            errorMessage={u.errorMessage}
                                            onSuccess={() => { setExpandedFixKey(null); setExpandedGroupKey(null); loadTrace(tracePage, traceSize); }}
                                            onSnackbar={setSnackbar}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </>
                                );
                              })}
                            </>
                          )}
                        </>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={traceTotal}
              page={tracePage}
              onPageChange={handleTracePage}
              rowsPerPage={traceSize}
              onRowsPerPageChange={handleTraceSize}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper", borderRadius: "0 0 10px 10px" }}
            />
          </>
        )}
      </Box>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity} sx={{ borderRadius: "10px", fontWeight: 600 }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

